import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../../../components/ui/Card';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-hot-toast';
import {
  Package, ShoppingCart, BarChart3, BarChart, TrendingUp, Layers,
  RefreshCw, Loader2, AlertCircle, Calendar, ChevronRight, ChevronDown, Wallet, Zap,
} from 'lucide-react';
import { LineChart } from '../../../components/charts';

const API = 'https://api.glowytics.com/api/meta-ads/product-spend-history';
const API_BREAKDOWN = 'https://api.glowytics.com/api/meta-ads/campaign-catalog-breakdown';
const API_LIVE = 'https://api.glowytics.com/api/meta-ads/live/product-spend';

const DATE_RANGES = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
];
const GROUP_COLORS = ['#4f46e5', '#0891b2', '#16a34a', '#ca8a04', '#dc2626', '#9333ea', '#db2777', '#0d9488', '#64748b', '#ea580c'];

// Trend chart shows a single metric (spend ~₺6K/day and revenue ~₺50K/day were unreadable on one axis)
const TREND_METRICS = [
  { key: 'spend', label: 'Spend', color: '#4f46e5' },
  { key: 'revenue', label: 'Revenue', color: '#059669' },
  { key: 'roas', label: 'ROAS', color: '#fb7185' },
  { key: 'purchases', label: 'Conversions', color: '#9333ea' },
];

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymd = (d) => d.toISOString().slice(0, 10);
const fmtTRY = (v) => (v == null ? '—' : `₺${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`);
const fmtNum = (v) => {
  const n = Number(v || 0);
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toLocaleString('en-US');
};
const shortDate = (s) => {
  const [, m, d] = (s || '').split('-');
  return d ? `${Number(d)} ${MONTHS_EN[Number(m) - 1]}` : s;
};

export default function MetaProductSpend() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : '');

  const [dateRange, setDateRange] = useState('last30');
  const [customStart, setCustomStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return d; });
  const [customEnd, setCustomEnd] = useState(new Date());
  const [campaignId, setCampaignId] = useState('');
  const [live, setLive] = useState(false);
  const [trendMetric, setTrendMetric] = useState('spend');
  const [breakdownDim, setBreakdownDim] = useState('category'); // 'category' | 'brand'
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [data, setData] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!brandId) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({ dateRange });
      if (dateRange === 'custom') { qs.set('startDate', ymd(customStart)); qs.set('endDate', ymd(customEnd)); }
      if (campaignId) qs.set('campaignId', campaignId);
      const headers = { 'X-Brand-ID': brandId };
      if (live) {
        // Anlık mod: doğrudan Meta Graph API'den çek (DB beklemez). Catalog-vs-Other ayrımı
        // ve aylık pivot DB türevi olduğu için anlık modda gelmez; o bölümler gizlenir.
        const res = await axios.get(`${API_LIVE}?${qs}`, { withCredentials: true, headers });
        setData(res.data);
        setBreakdown(null);
        if (!campaignId && res.data?.campaignOptions) setCampaignOptions(res.data.campaignOptions);
      } else {
        const [res, bres] = await Promise.all([
          axios.get(`${API}?${qs}`, { withCredentials: true, headers }),
          axios.get(`${API_BREAKDOWN}?${qs}`, { withCredentials: true, headers }),
        ]);
        setData(res.data);
        setBreakdown(bres.data);
        if (!campaignId && res.data?.campaignOptions) setCampaignOptions(res.data.campaignOptions);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      setError(msg);
      toast.error((live ? 'Anlık veri alınamadı: ' : 'Failed to load data: ') + msg);
    } finally {
      setLoading(false);
    }
  }, [brandId, dateRange, customStart, customEnd, campaignId, live]);

  useEffect(() => { fetchData(); }, [fetchData]);


  const totals = data?.totals || {};

  // Category ↔ Brand breakdown. Brand comes from the Meta catalog brand field (server-side);
  // only offer the Brand toggle when there's a real brand signal (not just an "Other" bucket).
  const brandRows = data?.brands || [];
  const hasBrandDim = brandRows.some((b) => b.brand && b.brand !== 'Diğer');
  const effBreakdownDim = breakdownDim === 'brand' && hasBrandDim ? 'brand' : 'category';
  const breakdownRows = effBreakdownDim === 'brand'
    ? brandRows.map((b) => ({ key: b.brand, label: b.brand, spend: b.spend || 0, revenue: b.revenue || 0, roas: b.roas }))
    : (data?.groups || []).map((g) => ({ key: g.group, label: g.group, spend: g.spend || 0, revenue: g.revenue || 0, roas: g.roas }));
  const maxGroupSpend = breakdownRows[0]?.spend || 1;
  const totalGroupSpend = breakdownRows.reduce((s, r) => s + (r.spend || 0), 0) || 1;

  const kpis = useMemo(() => ([
    { label: 'Spend', value: fmtTRY(totals.spend), icon: BarChart3, color: 'indigo' },
    { label: 'Revenue', value: fmtTRY(totals.revenue), icon: BarChart, color: 'emerald' },
    { label: 'ROAS', value: (totals.roas != null ? Number(totals.roas).toFixed(2) + 'x' : '—'), icon: TrendingUp, color: 'rose' },
    { label: 'Conversions', value: fmtNum(totals.purchases), icon: ShoppingCart, color: 'purple' },
    { label: 'Products', value: fmtNum(totals.products), icon: Package, color: 'blue' },
  ]), [totals]);

  const activeMetric = TREND_METRICS.find((m) => m.key === trendMetric) || TREND_METRICS[0];
  const trendChartData = useMemo(() => {
    const rows = data?.daily || [];
    const hex = activeMetric.color;
    const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
    return {
      labels: rows.map((d) => shortDate(d.date)),
      datasets: [{
        label: activeMetric.label,
        data: rows.map((d) => d[trendMetric]),
        borderColor: hex,
        backgroundColor: `rgba(${r},${g},${b},0.10)`,
        fill: true,
        tension: 0.35,
        pointRadius: 0,
        borderWidth: 2,
      }],
    };
  }, [data, trendMetric, activeMetric]);

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-50 border border-blue-100 shadow-sm rounded-xl">
            <Package className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Product Spend</h1>
            <p className="text-sm text-gray-500">
              {live
                ? 'Catalog / Advantage+ kampanyaları için ürün ve kategori bazında harcama — anlık (Meta API).'
                : 'Daily spend by product and category for Catalog / Advantage+ campaigns (logged data).'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setLive((v) => !v)}
            title={live ? 'Anlık mod açık — Meta API’den canlı çekiliyor' : 'Loglanmış (DB) veriyi gösteriyor'}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
              live
                ? 'bg-amber-50 border-amber-200 text-amber-700 shadow-sm'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <Zap className={`w-4 h-4 ${live ? 'fill-amber-400 text-amber-500' : ''}`} />
            {live ? 'Anlık' : 'Loglu'}
          </button>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300 min-w-[140px]"
          >
            {DATE_RANGES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-gray-400" />
              <DatePicker selected={customStart} onChange={setCustomStart} dateFormat="yyyy-MM-dd"
                className="px-2 py-2 w-28 bg-white border border-gray-200 rounded-xl text-sm" />
              <span className="text-gray-400">–</span>
              <DatePicker selected={customEnd} onChange={setCustomEnd} dateFormat="yyyy-MM-dd"
                className="px-2 py-2 w-28 bg-white border border-gray-200 rounded-xl text-sm" />
            </div>
          )}
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300 max-w-[260px]"
          >
            <option value="">All catalog campaigns</option>
            {campaignOptions.map((c) => (
              <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_name} ({fmtTRY(c.spend)})</option>
            ))}
          </select>
          <button onClick={fetchData} disabled={loading}
            className="p-2.5 bg-white border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-2xl border border-red-100">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button onClick={fetchData} className="underline ml-2">Try again</button>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((m, idx) => (
          <Card key={idx} className="p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl bg-${m.color}-50`}>
                  <m.icon className={`w-5 h-5 text-${m.color}-600`} />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{m.label}</p>
                <p className="text-2xl font-bold text-gray-900">{loading && !data ? '…' : m.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Daily trend — single metric for readability */}
      <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            <span className="font-medium text-gray-800">Daily trend</span>
          </div>
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            {TREND_METRICS.map((m) => (
              <button
                key={m.key}
                onClick={() => setTrendMetric(m.key)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-all ${trendMetric === m.key ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                style={trendMetric === m.key ? { color: m.color } : undefined}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4">
          {data?.daily?.length ? (
            <LineChart data={trendChartData} height={300} />
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">No data</div>
          )}
        </div>
      </Card>

      {/* Category / Brand breakdown */}
      <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-cyan-500" />
            <span className="font-medium text-gray-800">
              Spend &amp; revenue by {effBreakdownDim === 'brand' ? 'brand' : 'product group'}
            </span>
          </div>
          {hasBrandDim && (
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              {[{ key: 'category', label: 'Category' }, { key: 'brand', label: 'Brand' }].map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setBreakdownDim(o.key)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    effBreakdownDim === o.key ? 'bg-white text-cyan-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-center gap-3 pb-2 mb-1 border-b border-gray-100 text-xs font-semibold text-gray-400">
            <div className="w-28 shrink-0">{effBreakdownDim === 'brand' ? 'Brand' : 'Group'}</div>
            <div className="flex-1">Spend distribution</div>
            <div className="w-28 text-right shrink-0">Spend</div>
            <div className="w-28 text-right shrink-0">Revenue</div>
            <div className="w-16 text-right shrink-0">ROAS</div>
          </div>
          {breakdownRows.map((g, i) => {
            const pct = (g.spend / totalGroupSpend) * 100;
            return (
              <div key={g.key} className="flex items-center gap-3 py-1.5">
                <div className="w-28 text-sm text-gray-700 shrink-0 truncate" title={g.label}>{g.label}</div>
                <div className="flex-1 bg-gray-100 rounded-lg h-6 overflow-hidden">
                  <div className="h-6 rounded-lg" style={{ width: `${Math.max((g.spend / maxGroupSpend) * 100, 2)}%`, background: GROUP_COLORS[i % GROUP_COLORS.length] }} />
                </div>
                <div className="w-28 text-right text-sm shrink-0">
                  <span className="font-semibold text-gray-900">{fmtTRY(g.spend)}</span>
                  <span className="text-gray-400 ml-1 text-xs">%{pct.toFixed(0)}</span>
                </div>
                <div className="w-28 text-right text-sm shrink-0 text-emerald-700 font-medium">{fmtTRY(g.revenue)}</div>
                <div className={`w-16 text-right text-sm shrink-0 ${g.roas >= 1 ? 'text-emerald-700' : 'text-gray-500'}`}>{g.roas != null ? g.roas.toFixed(1) + 'x' : '—'}</div>
              </div>
            );
          })}
          {!breakdownRows.length && <div className="text-sm text-gray-400 py-4 text-center">No data</div>}
        </div>
      </Card>

      {/* Product table */}
      <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-purple-500" />
          <span className="font-medium text-gray-800">Top spending products ({Math.min(data?.products?.length || 0, 100)})</span>
        </div>
        <div className="overflow-auto max-h-[520px]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Product</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Group</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Spend</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Revenue</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {(data?.products || []).slice(0, 100).map((p) => (
                <tr key={p.product_id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-900 max-w-[420px] truncate" title={p.name}>{p.name}</td>
                  <td className="px-4 py-2 text-gray-500">{p.group}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{fmtTRY(p.spend)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{fmtTRY(p.revenue)}</td>
                  <td className={`px-4 py-2 text-right ${p.roas >= 1 ? 'text-emerald-700' : 'text-gray-500'}`}>{p.roas != null ? p.roas.toFixed(1) + 'x' : '—'}</td>
                </tr>
              ))}
              {!data?.products?.length && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Catalog-vs-Other ve aylık pivot DB türevidir; anlık modda gösterilmez. */}
      {!live && (<>
      {/* Campaign budget allocation — Catalog vs Other */}
      <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-indigo-500" />
            <span className="font-medium text-gray-800">Campaign budget allocation — Catalog vs Other</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5 text-gray-500"><span className="w-3 h-3 rounded-sm bg-indigo-500" /> Catalog</span>
            <span className="flex items-center gap-1.5 text-gray-500"><span className="w-3 h-3 rounded-sm bg-amber-400" /> Other</span>
            {breakdown?.totals && (
              <span className="text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                Catalog {fmtTRY(breakdown.totals.catalogSpend)} ({breakdown.totals.catalogPct}%)
              </span>
            )}
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Campaign</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Total</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Catalog</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Other</th>
                <th className="px-4 py-2.5 font-semibold text-gray-600 w-44">Split</th>
              </tr>
            </thead>
            <tbody>
              {(breakdown?.campaigns || []).map((c) => {
                const isOpen = !!expanded[c.campaign_id];
                const hasDetail = c.categories.length > 0 || c.otherSpend > 0;
                return (
                  <React.Fragment key={c.campaign_id}>
                    <tr className={`border-t border-gray-100 hover:bg-gray-50 ${hasDetail ? 'cursor-pointer' : ''}`}
                      onClick={() => hasDetail && setExpanded((e) => ({ ...e, [c.campaign_id]: !e[c.campaign_id] }))}>
                      <td className="px-4 py-2 text-gray-900">
                        <div className="flex items-center gap-1.5">
                          {hasDetail ? (isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />) : <span className="w-4 shrink-0" />}
                          <span className="truncate max-w-[360px]" title={c.campaign_name}>{c.campaign_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">{fmtTRY(c.totalSpend)}</td>
                      <td className="px-4 py-2 text-right text-indigo-700 whitespace-nowrap">{fmtTRY(c.catalogSpend)} <span className="text-gray-400 text-xs">%{c.catalogPct}</span></td>
                      <td className="px-4 py-2 text-right text-gray-600">{fmtTRY(c.otherSpend)}</td>
                      <td className="px-4 py-2">
                        <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden flex">
                          <div className="h-full bg-indigo-500" style={{ width: `${c.catalogPct}%` }} />
                          <div className="h-full bg-amber-400" style={{ width: `${100 - c.catalogPct}%` }} />
                        </div>
                      </td>
                    </tr>
                    {isOpen && c.categories.map((cat) => (
                      <tr key={c.campaign_id + cat.category} className="bg-indigo-50/30 border-t border-gray-50">
                        <td className="px-4 py-1.5 pl-12 text-gray-600">{cat.category}</td>
                        <td />
                        <td className="px-4 py-1.5 text-right text-gray-700">{fmtTRY(cat.spend)}</td>
                        <td /><td />
                      </tr>
                    ))}
                    {isOpen && c.otherSpend > 0 && (
                      <tr className="bg-amber-50/30 border-t border-gray-50">
                        <td className="px-4 py-1.5 pl-12 text-gray-500 italic">Other (non-catalog)</td>
                        <td /><td />
                        <td className="px-4 py-1.5 text-right text-gray-600">{fmtTRY(c.otherSpend)}</td>
                        <td />
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!breakdown?.campaigns?.length && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">{breakdown ? 'No data' : 'Loading…'}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      </>)}
    </div>
  );
}
